// Hero4.js - Contains functions for creating a modern, centered Hero section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a complete Hero section with nested elements
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the hero is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The hero elements
 */
export const createHeroElements = (
  getHierarchyName,
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const heroSectionClass = 'hero4-section';
  const heroContainerClass = 'hero4-container';
  const heroTitleClass = 'hero4-title';
  const heroSubtitleClass = 'hero4-subtitle';
  const heroButtonsClass = 'hero4-buttons';
  const heroPrimaryBtnClass = 'hero4-primary-btn';
  const heroCaptionClass = 'hero4-caption';
  const heroDecoration1Class = 'hero4-decoration1';
  const heroDecoration2Class = 'hero4-decoration2';
  const heroDecoration3Class = 'hero4-decoration3'; // New decoration
  const heroFloatingElem1Class = 'hero4-floating-elem1';
  const heroFloatingElem2Class = 'hero4-floating-elem2';
  const heroMainImageWrapperClass = 'hero4-main-image-wrapper';
  const heroMainImageClass = 'hero4-main-image';
  const heroTitleGradientClass = 'hero4-title-gradient';

  // Desktop Styles (default)
  const heroSectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '140px',
    paddingLeft: '20px',
    paddingRight: '20px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #D4A46F 100%)', // More vibrant: Indigo -> Purple -> Warm Tan/Gold
    color: '#ffffff', // Changed to white for better contrast on the new background
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    textAlign: 'center'
  };

  const heroDecoration1Styles = {
    position: 'absolute',
    top: '-10%', // Moved further out
    left: '5%',
    width: '300px', // Larger
    height: '300px',
    background: 'rgba(255, 255, 255, 0.1)', // Lighter, more ethereal
    borderRadius: '40% 60% 70% 30% / 50% 60% 40% 50%', // More irregular
    filter: 'blur(60px)',
    transform: 'rotate(-55deg) translateY(-50px)',
    zIndex: '0',
    opacity: '0.7'
  };

  const heroDecoration2Styles = {
    position: 'absolute',
    bottom: '-15%', // Moved further out
    right: '2%',
    width: '350px', // Larger
    height: '350px',
    background: 'rgba(255, 255, 255, 0.08)', // Lighter, more ethereal
    borderRadius: '70% 30% 40% 60% / 60% 70% 30% 40%', // More irregular
    filter: 'blur(70px)',
    transform: 'rotate(40deg) translateY(40px)',
    zIndex: '0',
    opacity: '0.6'
  };
  
  const heroDecoration3Styles = { // New subtle texture/pattern like element
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '500px',
    height: '500px',
    background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 70%)',
    borderRadius: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: '0',
    pointerEvents: 'none'
  };

  const heroFloatingElem1Styles = {
    position: 'absolute',
    top: '18%',
    right: '12%',
    padding: '14px 22px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '14px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
    color: '#4A3B8C', // Darker Purple for contrast
    fontSize: '15px',
    fontWeight: 'bold',
    zIndex: '2',
    animation: 'float 6s ease-in-out infinite',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };
  
  const heroFloatingElem2Styles = {
    position: 'absolute',
    bottom: '22%',
    left: '15%',
    padding: '12px 20px',
    backgroundColor: '#8A5A3F', // Muted Chocolate/Brown
    borderRadius: '12px',
    boxShadow: '0 8px 25px rgba(138, 90, 63, 0.35)',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
    zIndex: '2',
    animation: 'float-reverse 7.5s ease-in-out infinite',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const heroContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '880px',
    width: '100%',
    margin: '0 auto',
    gap: '20px',
    position: 'relative',
    zIndex: '1',
    padding: '30px', // Increased padding
    backgroundColor: 'rgba(0, 0, 0, 0.15)', // Darker, more contrasted glassmorphism
    borderRadius: '24px', // More rounded
    boxShadow: '0 15px 40px rgba(0,0,0,0.1)', // Slightly stronger shadow
    border: '1px solid rgba(255, 255, 255, 0.1)' // Subtle border for glass effect
  };
  
  const heroMainImageWrapperStyles = {
    width: '180px',
    height: '180px',
    borderRadius: '50%',
    overflow: 'hidden',
    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.25)', // Adjusted shadow for new bg
    marginBlock: '20px',
    border: '6px solid rgba(255,255,255,0.8)' // Thicker, slightly transparent border
  };
  
  const heroMainImageStyles = {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  };

  const heroTitleStyles = {
    margin: '0',
    fontSize: '60px',
    fontWeight: '800',
    color: '#ffffff', // White text due to darker background
    lineHeight: '1.15',
    letterSpacing: '-0.03em',
    textShadow: '0 2px 10px rgba(0,0,0,0.3)' // Text shadow for readability
  };
  
  const heroTitleGradientStyles = { // Keep this for title if a part is still gradient, else remove
    background: 'linear-gradient(90deg, #f0e6ff 0%, #ffe4b5 50%, #ffddc1 100%)', // Lighter gradient for contrast: Lavender -> Peach -> Light Orange
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  };

  const heroSubtitleStyles = {
    margin: '0',
    fontSize: '22px',
    fontWeight: '400',
    color: '#e0e0e0', // Lighter gray for readability on dark bg
    lineHeight: '1.6',
    maxWidth: '720px',
    textShadow: '0 1px 5px rgba(0,0,0,0.2)'
  };

  const heroButtonsStyles = {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    marginTop: '24px'
  };

  const heroPrimaryBtnStyles = {
    padding: '20px 48px',
    background: 'linear-gradient(90deg, #D4A46F 0%, #B08D57 100%)', // Gold/Tan gradient button
    color: '#4A3B8C', // Dark purple text for contrast
    border: '2px solid rgba(255,255,255,0.5)',
    borderRadius: '15px', 
    fontSize: '20px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 10px 25px -8px rgba(0, 0, 0, 0.4)', 
    textTransform: 'uppercase'
  };
  
  const heroCaptionStyles = {
    margin: '16px 0 0 0',
    fontSize: '15px',
    fontWeight: '500',
    color: '#f0f0f0', // Light color for caption
    lineHeight: '1.5',
    textShadow: '0 1px 3px rgba(0,0,0,0.2)'
  };

  // Tablet Styles
  const tabletHeroSectionStyles = {
    ...heroSectionStyles,
    paddingTop: '100px',
    paddingBottom: '120px',
  };

  const tabletHeroTitleStyles = {
    ...heroTitleStyles,
    fontSize: '52px'
  };
  
  const tabletHeroMainImageWrapperStyles = {
    ...heroMainImageWrapperStyles,
    width: '150px',
    height: '150px'
  };

  const tabletHeroSubtitleStyles = {
    ...heroSubtitleStyles,
    fontSize: '20px',
    maxWidth: '640px'
  };
  
  const tabletHeroPrimaryBtnStyles = {
    ...heroPrimaryBtnStyles,
    padding: '18px 40px',
    fontSize: '18px'
  };
  
  const tabletHeroFloatingElem1Styles = {
    ...heroFloatingElem1Styles,
    padding: '12px 18px',
    fontSize: '14px',
    right: '8%'
  };

  const tabletHeroFloatingElem2Styles = {
    ...heroFloatingElem2Styles,
    padding: '10px 16px',
    fontSize: '13px',
    left: '10%'
  };

  // Mobile Styles
  const mobileHeroSectionStyles = {
    ...heroSectionStyles,
    paddingTop: '80px',
    paddingBottom: '100px',
  };
  
  const mobileHeroContainerStyles = {
    ...heroContainerStyles,
    gap: '15px',
    padding: '20px', // Adjusted padding
    backgroundColor: 'rgba(0, 0, 0, 0.25)', // Slightly darker on mobile for text pop
  };
  
  const mobileHeroMainImageWrapperStyles = {
    ...heroMainImageWrapperStyles,
    width: '120px',
    height: '120px',
    marginBlock: '15px',
    borderWidth: '4px'
  };

  const mobileHeroTitleStyles = {
    ...heroTitleStyles,
    fontSize: '40px'
  };

  const mobileHeroSubtitleStyles = {
    ...heroSubtitleStyles,
    fontSize: '17px',
  };

  const mobileHeroPrimaryBtnStyles = {
    ...heroPrimaryBtnStyles,
    padding: '16px 32px',
    fontSize: '17px',
    width: '100%',
    maxWidth: '300px'
  };
  
  const mobileHeroCaptionStyles = {
    ...heroCaptionStyles,
    fontSize: '13px'
  };
  
  const mobileHeroFloatingElem1Styles = {
    ...heroFloatingElem1Styles,
    display: 'none'
  };
  
  const mobileHeroFloatingElem2Styles = {
    ...heroFloatingElem2Styles,
    bottom: '8%',
    left: '5%',
    padding: '10px 14px',
    fontSize: '12px'
  };
  
  const mobileHeroDecoration1Styles = {
    ...heroDecoration1Styles,
    width: '200px',
    height: '200px',
    filter: 'blur(50px)',
    opacity: '0.5'
  };

  const mobileHeroDecoration2Styles = {
    ...heroDecoration2Styles,
    width: '250px',
    height: '250px',
    filter: 'blur(60px)',
    opacity: '0.4'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };

    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(heroSectionClass, heroSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(heroDecoration1Class, heroDecoration1Styles);
    newRegistry[DEVICES.DESKTOP].set(heroDecoration2Class, heroDecoration2Styles);
    newRegistry[DEVICES.DESKTOP].set(heroDecoration3Class, heroDecoration3Styles);
    newRegistry[DEVICES.DESKTOP].set(heroFloatingElem1Class, heroFloatingElem1Styles);
    newRegistry[DEVICES.DESKTOP].set(heroFloatingElem2Class, heroFloatingElem2Styles);
    newRegistry[DEVICES.DESKTOP].set(heroContainerClass, heroContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(heroMainImageWrapperClass, heroMainImageWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(heroMainImageClass, heroMainImageStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTitleClass, heroTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTitleGradientClass, heroTitleGradientStyles); 
    newRegistry[DEVICES.DESKTOP].set(heroSubtitleClass, heroSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(heroButtonsClass, heroButtonsStyles);
    newRegistry[DEVICES.DESKTOP].set(heroPrimaryBtnClass, heroPrimaryBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroCaptionClass, heroCaptionStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(heroSectionClass, tabletHeroSectionStyles);
    newRegistry[DEVICES.TABLET].set(heroDecoration1Class, heroDecoration1Styles); // Apply base for tablet too, can be overridden
    newRegistry[DEVICES.TABLET].set(heroDecoration2Class, heroDecoration2Styles);
    newRegistry[DEVICES.TABLET].set(heroDecoration3Class, heroDecoration3Styles);
    newRegistry[DEVICES.TABLET].set(heroMainImageWrapperClass, tabletHeroMainImageWrapperStyles);
    newRegistry[DEVICES.TABLET].set(heroTitleClass, tabletHeroTitleStyles);
    newRegistry[DEVICES.TABLET].set(heroSubtitleClass, tabletHeroSubtitleStyles);
    newRegistry[DEVICES.TABLET].set(heroPrimaryBtnClass, tabletHeroPrimaryBtnStyles);
    newRegistry[DEVICES.TABLET].set(heroFloatingElem1Class, tabletHeroFloatingElem1Styles);
    newRegistry[DEVICES.TABLET].set(heroFloatingElem2Class, tabletHeroFloatingElem2Styles);
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(heroSectionClass, mobileHeroSectionStyles);
    newRegistry[DEVICES.MOBILE].set(heroDecoration1Class, mobileHeroDecoration1Styles);
    newRegistry[DEVICES.MOBILE].set(heroDecoration2Class, mobileHeroDecoration2Styles);
    newRegistry[DEVICES.MOBILE].set(heroDecoration3Class, heroDecoration3Styles); // ensure it is also applied on mobile
    newRegistry[DEVICES.MOBILE].set(heroFloatingElem1Class, mobileHeroFloatingElem1Styles);
    newRegistry[DEVICES.MOBILE].set(heroFloatingElem2Class, mobileHeroFloatingElem2Styles);
    newRegistry[DEVICES.MOBILE].set(heroContainerClass, mobileHeroContainerStyles);
    newRegistry[DEVICES.MOBILE].set(heroMainImageWrapperClass, mobileHeroMainImageWrapperStyles);
    newRegistry[DEVICES.MOBILE].set(heroTitleClass, mobileHeroTitleStyles);
    newRegistry[DEVICES.MOBILE].set(heroSubtitleClass, mobileHeroSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(heroPrimaryBtnClass, mobileHeroPrimaryBtnStyles);
    newRegistry[DEVICES.MOBILE].set(heroCaptionClass, mobileHeroCaptionStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    heroSectionClass,
    heroDecoration1Class,
    heroDecoration2Class,
    heroDecoration3Class,
    heroFloatingElem1Class,
    heroFloatingElem2Class,
    heroContainerClass,
    heroMainImageWrapperClass,
    heroMainImageClass,
    heroTitleClass,
    heroTitleGradientClass,
    heroSubtitleClass,
    heroButtonsClass,
    heroPrimaryBtnClass,
    heroCaptionClass
  ]));

  // Generate IDs for all elements
  const timestamp = Date.now();
  const heroSectionId = timestamp;
  const decoration1ID = timestamp + 1;
  const decoration2ID = timestamp + 2;
  const decoration3ID = timestamp + 3;
  const floatingElem1ID = timestamp + 4;
  const floatingElem2ID = timestamp + 5;
  const containerID = timestamp + 6;
  const mainImageWrapperID = timestamp + 7;
  const mainImageID = timestamp + 8;
  const titleID = timestamp + 9;
  const titleGradientID = timestamp + 10; // Keep if title gradient span is used
  const subtitleID = timestamp + 11;
  const buttonsWrapperID = timestamp + 12;
  const primaryButtonID = timestamp + 13;
  const captionID = timestamp + 14;

  const newElements = [
    {
      id: heroSectionId,
      builderId: uuidv4(),
      type: 'section',
      name: 'Hero Section 4',
      parentId: null,
      className: heroSectionClass,
      styles: {},
      children: [decoration1ID, decoration2ID, decoration3ID, floatingElem1ID, floatingElem2ID, containerID],
      text: '',
      attributes: {
        'data-element': 'hero-section',
        'data-component': 'section',
        'data-section-type': 'hero',
        'data-page-id': currentPageId
      }
    },
    {
      id: decoration1ID,
      builderId: uuidv4(),
      type: 'div',
      name: 'Decoration 1',
      parentId: heroSectionId,
      hierarchyName: getHierarchyName('div'),
      className: heroDecoration1Class,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'hero-decoration'
      }
    },
    {
      id: decoration2ID,
      builderId: uuidv4(),
      type: 'div',
      name: 'Decoration 2',
      parentId: heroSectionId,
      hierarchyName: getHierarchyName('div'),
      className: heroDecoration2Class,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'hero-decoration'
      }
    },
    {
      id: decoration3ID,
      builderId: uuidv4(),
      type: 'div',
      name: 'Decoration 3 (Texture)',
      parentId: heroSectionId,
      hierarchyName: getHierarchyName('div'),
      className: heroDecoration3Class,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'hero-bg-texture'
      }
    },
    {
      id: floatingElem1ID,
      builderId: uuidv4(),
      type: 'div',
      name: 'Floating Element 1',
      parentId: heroSectionId,
      hierarchyName: getHierarchyName('div'),
      className: heroFloatingElem1Class,
      styles: {},
      children: [],
      text: '🚀 Launch Faster!', // Updated text
      attributes: {
        'data-element': 'hero-floating-text'
      }
    },
    {
      id: floatingElem2ID,
      builderId: uuidv4(),
      type: 'div',
      name: 'Floating Element 2',
      parentId: heroSectionId,
      hierarchyName: getHierarchyName('div'),
      className: heroFloatingElem2Class,
      styles: {},
      children: [],
      text: '⭐ Top Rated Designs', // Updated text
      attributes: {
        'data-element': 'hero-floating-badge'
      }
    },
    {
      id: containerID,
      builderId: uuidv4(),
      type: 'div',
      name: 'Hero Container',
      parentId: heroSectionId,
      hierarchyName: getHierarchyName('div'),
      className: heroContainerClass,
      styles: {},
      children: [mainImageWrapperID, titleID, subtitleID, buttonsWrapperID, captionID],
      text: '',
      attributes: {
        'data-element': 'hero-container'
      }
    },
    {
      id: mainImageWrapperID,
      builderId: uuidv4(),
      type: 'div',
      name: 'Main Image Wrapper',
      parentId: containerID,
      hierarchyName: getHierarchyName('div'),
      className: heroMainImageWrapperClass,
      styles: {},
      children: [mainImageID],
      text: '',
      attributes: {
        'data-element': 'hero-image-wrapper'
      }
    },
    {
      id: mainImageID,
      builderId: uuidv4(),
      type: 'image',
      name: 'Hero Image',
      parentId: mainImageWrapperID,
      hierarchyName: getHierarchyName('image'),
      className: heroMainImageClass,
      styles: {},
      children: [],
      text: '',
      src: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&q=80', // Different placeholder
      attributes: {
        'data-element': 'hero-image',
        alt: 'Tech Abstract Background'
      }
    },
    {
      id: titleID,
      builderId: uuidv4(),
      type: 'h1',
      name: 'Hero Title',
      parentId: containerID,
      hierarchyName: getHierarchyName('h1'),
      className: heroTitleClass, 
      styles: {}, // Direct white color, no gradient span needed if full title is white
      children: [titleGradientID], // Using the gradient span for part of the title
      text: 'Unleash Your ', // Text before gradient part
      attributes: {
        'data-element': 'hero-title'
      },
      interactions: {
        events: [{
          id: timestamp + 15, // Adjusted ID due to new element
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'fade-in-down',
            duration: '0.9s',
            delay: '0.1s',
            iterations: '1'
          }
        }]
      }
    },
    {
        id: titleGradientID,
        builderId: uuidv4(),
        type: 'span',
        name: 'Title Gradient Text',
        parentId: titleID,
        hierarchyName: getHierarchyName('span'),
        className: heroTitleGradientClass,
        styles: { display: 'block' }, // Or display: 'inline-block' based on desired layout
        children: [],
        text: 'Creative Vision',
        attributes: {
          'data-element': 'hero-title-gradient'
        }
    },
    {
      id: subtitleID,
      builderId: uuidv4(),
      type: 'p',
      name: 'Hero Subtitle',
      parentId: containerID,
      hierarchyName: getHierarchyName('p'),
      className: heroSubtitleClass,
      styles: {},
      children: [],
      text: 'Build breathtaking websites with unparalleled ease. Our platform is designed to bring your most ambitious ideas to life, pixel by pixel.',
      attributes: {
        'data-element': 'hero-subtitle'
      },
      interactions: {
        events: [{
          id: timestamp + 16,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'fade-in-up',
            duration: '0.9s',
            delay: '0.2s',
            iterations: '1'
          }
        }]
      }
    },
    {
      id: buttonsWrapperID,
      builderId: uuidv4(),
      type: 'div',
      name: 'Hero Buttons',
      parentId: containerID,
      hierarchyName: getHierarchyName('div'),
      className: heroButtonsClass,
      styles: {},
      children: [primaryButtonID],
      text: '',
      attributes: {
        'data-element': 'hero-buttons'
      }
    },
    {
      id: primaryButtonID,
      builderId: uuidv4(),
      type: 'button',
      name: 'Primary Button',
      parentId: buttonsWrapperID,
      hierarchyName: getHierarchyName('button'),
      className: heroPrimaryBtnClass,
      styles: {},
      children: [],
      text: 'Start Designing',
      attributes: {
        'data-element': 'primary-button'
      },
      stateClassName: {
        hover: '', 
        pressed: '',
        focused: ''
      },
      currentState: 'default',
      interactions: {
        events: [{
          id: timestamp + 17,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'pulse-once', 
            duration: '1s',
            delay: '0.4s',
            iterations: '1'
          }
        }]
      }
    },
    {
      id: captionID,
      builderId: uuidv4(),
      type: 'p',
      name: 'Hero Caption',
      parentId: containerID,
      hierarchyName: getHierarchyName('p'),
      className: heroCaptionClass,
      styles: {},
      children: [],
      text: 'Experience the future of web creation. Free trial, no commitment.',
      attributes: {
        'data-element': 'hero-caption'
      },
      interactions: {
        events: [{
          id: timestamp + 18,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'fade-in-up',
            duration: '0.9s',
            delay: '0.5s',
            iterations: '1'
          }
        }]
      }
    }
  ];

  return {
    heroSection: newElements[0],
    elements: newElements
  };
};

export default createHeroElements; 